import {Box, Grid, Typography} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import {Parameter} from '../../../Core/mobx/ParameterStore/ParameterStore';
import {Prequal} from '../../../Core/mobx/ParameterStore/Prequal';
import {observer} from 'mobx-react-lite';
import {labelTextStyle, SwitchSegmentedYesNo} from '../../../Core/UI';
import {SelectInputElement} from './SelectInputElement';
import {fieldOptions} from '@/Core/mobx/ParameterStore/PrequalXref';
import {Button} from '../../../components/ui/button';
import {DatePicker, MuiPickersUtilsProvider} from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';

const useStyles = makeStyles({
    icon: {
        marginRight: 4,
    },
    approved: {
        color: 'green',
    },
    denied: {
        color: 'red',
    },
    sectionTitle: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: 46,
        width: 46,
        borderRadius: '50%',
        backgroundColor: '#e8edec',
        color: '#456d69',
        marginRight: 21,
        fontSize: 26,
        textAlign: 'center',
    },
    sectionTitleWrapper: {
        display: 'flex',
        alignItems: 'center',
        textTransform: 'capitalize',
    },
    extraQuestionsBox: {
        margin: '56px 8px',
        boxShadow: '0px 0px 5px 0px #00000033',
    },
    extraQuestionsLabel: {
        marginBottom: 16,
        fontFamily: 'Fredoka',
        fontSize: 18,
        fontWeight: 500,
        lineHeight: '100%',
    },
    dateLastUsedLabel: {
        marginBottom: 12,
        display: 'inline-flex',
        gap: 6,
    },
    noneOfTheAbove: {
        minWidth: 320,
        maxWidth: 600,
        flexShrink: 1,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        textTransform: 'capitalize',
        whiteSpace: 'normal',
        fontFamily: 'FredokaRegular',
        fontSize: 18,
        fontWeight: 500,
        lineHeight: '160%',
        letterSpacing: 0,
        color: '#838383',
    },
    markAllButton: {
        width: 233,
        height: 48,
        textTransform: 'capitalize',
        fontFamily: 'FredokaRegular',
        fontSize: 16,
        lineHeight: '160%',
        letterSpacing: 0,
        color: '#838383',
        borderColor: '#838383',
        '&:hover': {
            backgroundColor: '#838383',
            color: 'white',
        },
    },
});

type BehaviorProps = {
    valueChanged: (p?: Parameter, value?: any) => void;
    parameters: Prequal;
    hideNumbers?: boolean;
};

const Behavior = observer(({valueChanged, parameters, hideNumbers}: BehaviorProps) => {
    const classes = useStyles();

    const required = (value?: string) => (
        <span>
            {value && value} <span style={{color: 'red'}}>*</span>
        </span>
    );

    const setBehaviorsToNo = () => {
        valueChanged(parameters.DrugorAlcoholDependenceWithdrawal, false);
        valueChanged(parameters.DrugorAlcoholDependenceWithdrawalTreatmentStatus, '');
        valueChanged(parameters.DrugorAlcoholDependenceWithdrawalTreatmentDate, '');
        valueChanged(parameters.DUI, false);
        valueChanged(parameters.DUIDate, '');
        valueChanged(parameters.License, false);
        valueChanged(parameters.suspendedLicense, '');
        valueChanged(parameters.Criminaloffense, false);
        valueChanged(parameters.criminalOffenseDate, '');
        valueChanged(parameters.criminalOffenseChargeType, '');
        valueChanged(parameters.criminalOffenseProbation, '');
        valueChanged(parameters.Bankruptcy, false);
        valueChanged(parameters.bankruptcyOpen, '');
    };

    return (
        <Grid container style={{marginBottom: 24}} spacing={2}>
            <Grid item>
                <div className={classes.sectionTitleWrapper}>
                    {!hideNumbers && <span className={classes.sectionTitle}>4</span>}
                    <Typography
                        style={{
                            fontSize: 24,
                            fontWeight: 700,
                            fontFamily: 'LoraSemiBold',
                            lineHeight: '120%',
                            letterSpacing: 0,
                        }}
                    >
                        Behavior
                    </Typography>
                </div>
            </Grid>
            <div style={{width: '100%', marginBottom: 12}} />
            <Grid item xs={12}>
                <SwitchSegmentedYesNo
                    param={{
                        label: 'Drug or Alcohol Dependence / Withdrawal',
                        ...parameters.DrugorAlcoholDependenceWithdrawal,
                        name: 'DrugorAlcoholDependenceWithdrawal',
                    }}
                    required={required()}
                    valueChanged={valueChanged}
                    horizontal
                />
            </Grid>
            {parameters.DrugorAlcoholDependenceWithdrawal.value && (
                <Grid
                    container
                    item
                    xs={12}
                    spacing={2}
                    className={classes.extraQuestionsBox}
                    style={{
                        padding: '17px 25px 43px',
                    }}
                >
                    <Grid item xs={12}>
                        <Typography className={classes.extraQuestionsLabel}>
                            Drug or Alcohol Dependence / Withdrawal
                        </Typography>
                    </Grid>
                    <Grid item xs={4}>
                        <SelectInputElement
                            param={{
                                label: 'Treatment status',
                                options: fieldOptions.drugorAlcoholDependenceWithdrawalTreatmentStatus.options,
                                ...parameters.DrugorAlcoholDependenceWithdrawalTreatmentStatus,
                                name: 'DrugorAlcoholDependenceWithdrawalTreatmentStatus',
                            }}
                            required={required()}
                            valueChanged={valueChanged}
                            newLabelType
                            showExtraLabel
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <Typography className={classes.dateLastUsedLabel} style={labelTextStyle}>
                            <span>{required()}</span>
                            <span>Date Last Used</span>
                        </Typography>
                        <MuiPickersUtilsProvider utils={DateFnsUtils}>
                            <DatePicker
                                inputVariant="outlined"
                                variant="inline"
                                openTo="year"
                                views={['year', 'month']}
                                placeholder="MM/yyyy"
                                style={{width: '100%'}}
                                value={parameters.DrugorAlcoholDependenceWithdrawalTreatmentDate?.value || null}
                                onChange={(date) =>
                                    valueChanged(parameters.DrugorAlcoholDependenceWithdrawalTreatmentDate, date)
                                }
                                InputProps={{
                                    style: {
                                        height: 48,
                                    },
                                }}
                                InputLabelProps={{
                                    shrink: !!parameters.DrugorAlcoholDependenceWithdrawalTreatmentDate?.value,
                                    style: parameters.DrugorAlcoholDependenceWithdrawalTreatmentDate?.value
                                        ? {}
                                        : {
                                              paddingLeft: 14,
                                              top: '50%',
                                              transform: 'translateY(-50%)',
                                          },
                                }}
                            />
                        </MuiPickersUtilsProvider>
                    </Grid>
                    <Grid item xs={4} />
                </Grid>
            )}
            <Grid item xs={12}>
                <SwitchSegmentedYesNo
                    param={{
                        label: 'DUI in Last 5 years?',
                        ...parameters.DUI,
                        name: 'DUI',
                    }}
                    required={required()}
                    valueChanged={valueChanged}
                    horizontal
                />
            </Grid>
            {parameters.DUI.value && (
                <Grid
                    container
                    item
                    xs={12}
                    spacing={2}
                    className={classes.extraQuestionsBox}
                    style={{
                        padding: '17px 25px 43px',
                    }}
                >
                    <Grid item xs={12}>
                        <Typography className={classes.extraQuestionsLabel}>DUI</Typography>
                    </Grid>
                    <Grid item xs={4}>
                        <Typography className={classes.dateLastUsedLabel} style={labelTextStyle}>
                            <span>{required()}</span>
                            <span>When did it occur?</span>
                        </Typography>
                        <MuiPickersUtilsProvider utils={DateFnsUtils}>
                            <DatePicker
                                inputVariant="outlined"
                                variant="inline"
                                openTo="year"
                                views={['year', 'month']}
                                format="MM/yyyy"
                                style={{width: '100%', marginTop: 8}}
                                placeholder="MM/yyyy"
                                value={parameters.DUIDate?.value || null}
                                maxDate={new Date()}
                                onChange={(date) => valueChanged(parameters.DUIDate, date)}
                                InputProps={{
                                    style: {
                                        height: 48,
                                    },
                                }}
                                InputLabelProps={{
                                    shrink: !!parameters.DUIDate?.value,
                                    style: parameters.DUIDate?.value
                                        ? {}
                                        : {
                                              paddingLeft: 14,
                                              top: '50%',
                                              transform: 'translateY(-50%)',
                                          },
                                }}
                            />
                        </MuiPickersUtilsProvider>
                    </Grid>
                </Grid>
            )}
            <Grid item xs={12}>
                <SwitchSegmentedYesNo
                    param={{
                        label: 'Suspended License',
                        ...parameters.License,
                        name: 'License',
                    }}
                    required={required()}
                    valueChanged={valueChanged}
                    horizontal
                />
            </Grid>
            {parameters.License.value && (
                <Grid
                    container
                    item
                    xs={12}
                    spacing={2}
                    className={classes.extraQuestionsBox}
                    style={{
                        padding: '17px 25px 43px',
                    }}
                >
                    <Grid item xs={12}>
                        <Typography className={classes.extraQuestionsLabel}>Suspended License</Typography>
                    </Grid>
                    <Grid item xs={4}>
                        <SwitchSegmentedYesNo
                            param={{
                                label: 'Currently Suspended',
                                ...parameters.suspendedLicense,
                                name: 'suspendedLicense',
                            }}
                            required={required()}
                            valueChanged={valueChanged}
                        />
                    </Grid>
                </Grid>
            )}
            <Grid item xs={12}>
                <SwitchSegmentedYesNo
                    param={{
                        label: 'Ever been convicted of a criminal offense?',
                        ...parameters.Criminaloffense,
                        name: 'Criminaloffense',
                    }}
                    required={required()}
                    valueChanged={valueChanged}
                    horizontal
                />
            </Grid>
            {parameters.Criminaloffense.value && (
                <Grid
                    container
                    item
                    xs={12}
                    spacing={2}
                    className={classes.extraQuestionsBox}
                    style={{
                        padding: '17px 25px 43px',
                    }}
                >
                    <Grid item xs={12}>
                        <Typography className={classes.extraQuestionsLabel}>Criminal Offense</Typography>
                    </Grid>
                    <Grid item xs={4}>
                        <Typography className={classes.dateLastUsedLabel} style={labelTextStyle}>
                            <span>{required()}</span>
                            <span>When did it occur?</span>
                        </Typography>
                        <MuiPickersUtilsProvider utils={DateFnsUtils}>
                            <DatePicker
                                inputVariant="outlined"
                                variant="inline"
                                openTo="year"
                                views={['year', 'month']}
                                format="MM/yyyy"
                                placeholder="MM/yyyy"
                                style={{width: '100%'}}
                                value={parameters.criminalOffenseDate?.value || null}
                                maxDate={new Date()}
                                onChange={(date) => valueChanged(parameters.criminalOffenseDate, date)}
                                InputProps={{
                                    style: {
                                        height: 48,
                                    },
                                }}
                                InputLabelProps={{
                                    shrink: !!parameters.criminalOffenseDate.value,
                                    style: parameters.criminalOffenseDate.value
                                        ? {}
                                        : {
                                              paddingLeft: 14,
                                              top: '50%',
                                              transform: 'translateY(-50%)',
                                          },
                                }}
                            />
                        </MuiPickersUtilsProvider>
                    </Grid>
                    <Grid item xs={4}>
                        <SelectInputElement
                            param={{
                                label: 'Type of Offense Charge',
                                options: fieldOptions.criminalOffenseChargeType.options,
                                ...parameters.criminalOffenseChargeType,
                                name: 'criminalOffenseChargeType',
                            }}
                            required={required()}
                            valueChanged={valueChanged}
                            newLabelType
                            showExtraLabel
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <SwitchSegmentedYesNo
                            param={{
                                label: 'Currently on Parole/Probation',
                                ...parameters.criminalOffenseProbation,
                                name: 'criminalOffenseProbation',
                            }}
                            required={required()}
                            valueChanged={valueChanged}
                        />
                    </Grid>
                </Grid>
            )}
            <Grid item xs={12}>
                <SwitchSegmentedYesNo
                    param={{
                        label: 'Have you declared bankruptcy?',
                        ...parameters.Bankruptcy,
                        name: 'Bankruptcy',
                    }}
                    required={required()}
                    valueChanged={valueChanged}
                    horizontal
                />
            </Grid>
            {parameters.Bankruptcy.value && (
                <Grid
                    container
                    item
                    xs={12}
                    spacing={2}
                    className={classes.extraQuestionsBox}
                    style={{
                        padding: '17px 25px 43px',
                    }}
                >
                    <Grid item xs={12}>
                        <Typography className={classes.extraQuestionsLabel}>Bankruptcy</Typography>
                    </Grid>
                    <Grid item xs={4}>
                        <SwitchSegmentedYesNo
                            param={{
                                label: 'Is it open?',
                                ...parameters.bankruptcyOpen,
                                name: 'bankruptcyOpen',
                            }}
                            required={required()}
                            valueChanged={valueChanged}
                        />
                    </Grid>
                    <Grid item xs={8} />
                </Grid>
            )}
            <Grid item xs={12} style={{padding: '0px 8px'}}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography className={classes.noneOfTheAbove}>Client has none of the above</Typography>
                    <Button
                        variant="outline"
                        color="primary"
                        onClick={() => {
                            setBehaviorsToNo();
                        }}
                        className={classes.markAllButton}
                    >
                        Mark all as No
                    </Button>
                </Box>
            </Grid>
        </Grid>
    );
});

export default Behavior;
